import React, { useEffect, useState } from 'react';
import { useRenameApiKeyMutation } from '@hooks/requests/queries/use_rename_api_key_mutation';
import { useToast } from '@hooks/useToast';
import { ApiError } from '@hooks/requests/utils/_partner_api_client';
import type { PartnerApiKey } from '@hooks/requests/queries/use_partner_api_keys_query';
import { Modal, Button, Input } from '../ui';

const NAME_MAX_LENGTH = 30;

interface RenameApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey: PartnerApiKey | null;
}

/**
 * Rename API key modal (sc-45257, AC2-AC7).
 *
 * A single-field modal, pre-filled with the key's current name (or empty
 * if unnamed) — same 30-character cap as Create (AC3). AC4: renaming only
 * ever updates the Name field, never the masked key value, the key's
 * ability to authenticate, or anything about its Client Grant, so there's
 * nothing here beyond the one input and a save/cancel pair.
 *
 * AC5 (blank, or over 30 characters) is a client-side inline error on the
 * field itself, same treatment as `CreateApiKeyModal`'s "Key name" — a
 * toast is for AC6 (the save call itself failing), a different failure
 * mode with a different treatment: the modal stays open with the typed
 * name still in the field either way, but AC6 explicitly calls for a
 * toast rather than an inline banner, so the admin sees the failure
 * surface the same way `ApiKeysTable`'s 1-key-floor toast already does.
 * Canceling reverts to the previous name (AC6) simply by discarding this
 * component's local state — nothing has been sent yet.
 *
 * AC6's toast copy is fixed by Figma (node 3177:16104, "We couldn't
 * rename this key. Please try again.") for every failure except the
 * stale-target 404 — unlike Revoke/Create, which use
 * `describeMutationError`'s shared per-status copy (a 502/503 there
 * reads "This isn't available right now..."), Rename's error state in
 * Figma has just the one generic message, so it's applied directly here
 * rather than through that shared helper.
 */
const RenameApiKeyModal: React.FC<RenameApiKeyModalProps> = ({ isOpen, onClose, apiKey }) => {
  const [name, setName] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const renameMutation = useRenameApiKeyMutation();
  const { showToast } = useToast();

  // Re-seed the field from the target key's current name every time the
  // modal opens for a (possibly different) row, rather than once on mount
  // — `apiKey` changes without this component remounting, since
  // `ApiKeysPage` reuses one modal instance for every row.
  useEffect(() => {
    if (isOpen) {
      setName(apiKey?.name ?? '');
      setSubmitAttempted(false);
    }
  }, [isOpen, apiKey]);

  const handleClose = () => {
    onClose();
  };

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX_LENGTH;

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!apiKey || !isNameValid) return;

    try {
      await renameMutation.mutateAsync({ maskedKey: apiKey.maskedKey, name: trimmedName });
      showToast(`API Key renamed to "${trimmedName}"`);
      onClose();
    } catch (err) {
      // AC6: a toast, not an inline banner — the modal stays open with the
      // typed name intact so the admin can retry by clicking Save again.
      // Figma's error toast for this modal has one fixed generic message
      // (node 3177:16104) rather than per-status copy — the only case that
      // gets its own wording is the stale-target 404.
      const message =
        err instanceof ApiError && err.status === 404
          ? `${apiKey.name || 'This key'} no longer exists in this organization.`
          : "We couldn't rename this key. Please try again.";
      showToast(message, 'error');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Rename API key"
      headerVariant="tinted"
      className="api-keys-modal"
      bodyClassName="api-keys-modal__body"
      footerClassName="api-keys-modal__foot"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="au-modal-pill-btn--confirm"
            onClick={handleSubmit}
            isLoading={renameMutation.isPending}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Key name"
          placeholder="e.g. Warehouse sync"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          onChange={(event) => setName(event.target.value)}
          error={submitAttempted && !isNameValid ? 'Enter a name up to 30 characters' : undefined}
          hint={
            submitAttempted && !isNameValid ? undefined : `${trimmedName.length}/${NAME_MAX_LENGTH}`
          }
          hintAlign="right"
          autoFocus
        />
      </div>
    </Modal>
  );
};

export default RenameApiKeyModal;
